import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buildWhatsAppUrl, isWhatsAppConfigured } from "@/lib/whatsapp";
import { getWeChatId, isWeChatConfigured, copyWeChatId } from "@/lib/wechat";

const FloatingActions = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [waHovered, setWaHovered] = useState(false);
  const [wcHovered, setWcHovered] = useState(false);
  const [wcCopied, setWcCopied] = useState(false);

  const whatsappUrl = buildWhatsAppUrl();
  const whatsappReady = isWhatsAppConfigured();
  const wechatReady = isWeChatConfigured();

  // WeChat has no web deep-link, so the action is copy-the-ID (matches product detail).
  const handleWeChat = async () => {
    if (!wechatReady) return;
    if (await copyWeChatId()) {
      setWcCopied(true);
      setTimeout(() => setWcCopied(false), 2000);
    }
  };

  // Product detail pages have their own mobile sticky action bar — hide on mobile to avoid redundancy.
  const isProductDetail = /^\/products\/[^/]+$/.test(location.pathname);
  const hideOnMobile = isProductDetail ? "hidden md:flex" : "flex";

  return (
    <div
      className={`fixed bottom-6 end-5 z-40 ${hideOnMobile} flex-col items-end gap-3`}
      role="group"
      aria-label={t("whatsapp.cta")}
    >
      {/* WeChat button — click to copy the WeChat ID */}
      <div className="relative flex items-center justify-end">
        <button
          type="button"
          onClick={handleWeChat}
          aria-label={t("wechat.cta")}
          onMouseEnter={() => setWcHovered(true)}
          onMouseLeave={() => setWcHovered(false)}
          onFocus={() => setWcHovered(true)}
          onBlur={() => setWcHovered(false)}
          title={wechatReady ? `WeChat ID: ${getWeChatId()}` : "WeChat ID pending confirmation."}
          className={`flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 overflow-hidden ${
            wcHovered ? "gap-2.5 px-5 py-0 h-14 w-auto min-w-[56px]" : "w-14 h-14"
          }`}
          style={{ boxShadow: "0 4px 18px rgba(180,83,9,0.35)" }}
        >
          <i className={`${wcCopied ? "ri-check-line" : "ri-wechat-line"} text-2xl shrink-0`} />
          <span
            className={`text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
              wcHovered ? "opacity-100 max-w-xs" : "opacity-0 max-w-0"
            }`}
          >
            {t("wechat.cta")}
          </span>
        </button>
      </div>

      {/* WhatsApp button */}
      <div className="relative flex items-center justify-end">
        <a
          href={whatsappReady ? whatsappUrl : "/contact"}
          target={whatsappReady ? "_blank" : undefined}
          rel={whatsappReady ? "noopener noreferrer" : undefined}
          aria-label={t("whatsapp.cta")}
          onMouseEnter={() => setWaHovered(true)}
          onMouseLeave={() => setWaHovered(false)}
          onFocus={() => setWaHovered(true)}
          onBlur={() => setWaHovered(false)}
          title={
            whatsappReady
              ? undefined
              : "WhatsApp number pending — tap to open Contact page."
          }
          className={`flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-600 text-white transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 overflow-hidden ${
            waHovered ? "gap-2.5 px-5 py-0 h-14 w-auto min-w-[56px]" : "w-14 h-14"
          }`}
          style={{ boxShadow: "0 4px 18px rgba(180,83,9,0.35)" }}
        >
          <i className="ri-whatsapp-line text-2xl shrink-0" />
          <span
            className={`text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
              waHovered ? "opacity-100 max-w-xs" : "opacity-0 max-w-0"
            }`}
          >
            {t("whatsapp.cta")}
          </span>
        </a>
      </div>
    </div>
  );
};

export default FloatingActions;
